import React, { useEffect, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  Tabs, TabList, TabPanels, Tab, TabPanel,
  Box, Image, Button, Text, Flex, useToast, Grid, Badge
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAvailableAvatars, updateUserAvatar } from '../../Redux/Login/action';
import BurrowSpinner from '../UI/BurrowSpinner';

const AvatarSelector = ({ isOpen, onClose, currentAvatar }) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const [selectedAvatar, setSelectedAvatar] = useState(currentAvatar);
  const [saving, setSaving] = useState(false);

  const { tiers = [], unlockedTiers = [], loading, error } = useSelector(state => state.auth.avatarSelector || {});

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchAvailableAvatars());
      setSelectedAvatar(currentAvatar);
    }
  }, [isOpen, dispatch, currentAvatar]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const success = await dispatch(updateUserAvatar(selectedAvatar));
      if (success) {
        toast({ title: "Avatar updated!", status: "success", duration: 2000 });
        onClose();
      } else {
        // Error already dispatched in the action creator
        toast({ title: "Failed to update avatar", status: "error", duration: 3000 });
      }
    } catch (e) {
      toast({ title: "Error", description: e.message, status: "error", duration: 3000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="xl">Select Your Avatar</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {loading ? (
            <Flex justify="center" align="center" minH="300px"><BurrowSpinner /></Flex>
          ) : error ? (
            <Text color="red.500">{error}</Text>
          ) : (
            <Tabs isFitted variant="enclosed">
              <TabList mb="1em">
                {tiers.map(tier => (
                  <Tab key={tier.tier} isDisabled={!tier.isUnlocked} fontSize="lg" py={3}>
                    Tier {tier.tier} {tier.isUnlocked ? "" : (
                      <Badge ml={2} colorScheme="gray" fontSize="md">
                        Unlocks at Level {tier.requiredLevel || '???'}
                      </Badge>
                    )}
                  </Tab>
                ))}
              </TabList>
              <TabPanels 
                maxH="450px" 
                overflowY="auto"
                css={{
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    width: '8px',
                    background: 'rgba(0,0,0,0.1)',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(0,0,0,0.2)',
                    borderRadius: '24px',
                  },
                }}
              >
                {tiers.map(tier => (
                  <TabPanel key={tier.tier} p={4}>
                    <Grid 
                      templateColumns="repeat(auto-fill, minmax(120px, 1fr))" 
                      gap={6}
                      pb={3}
                    >
                      {tier.avatars.map(url => (
                        <Box
                          key={url}
                          borderWidth={selectedAvatar === url ? "4px" : "2px"}
                          borderColor={selectedAvatar === url ? "blue.500" : "gray.300"}
                          borderRadius="xl"
                          p={3}
                          opacity={tier.isUnlocked ? 1 : 0.4}
                          cursor={tier.isUnlocked ? "pointer" : "not-allowed"}
                          onClick={() => tier.isUnlocked && setSelectedAvatar(url)}
                          transition="all 0.2s"
                          _hover={tier.isUnlocked ? { 
                            transform: "scale(1.05)", 
                            shadow: "lg",
                            borderColor: "blue.400" 
                          } : {}}
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          bg={selectedAvatar === url ? "blue.50" : "transparent"}
                        >
                          <Image 
                            src={url} 
                            alt="avatar" 
                            borderRadius="full" 
                            boxSize="100px"
                            objectFit="cover"
                          />
                        </Box>
                      ))}
                    </Grid>
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} size="lg">Cancel</Button>
          <Button 
            colorScheme="blue" 
            onClick={handleSave} 
            isLoading={saving} 
            isDisabled={selectedAvatar === currentAvatar || !selectedAvatar}
            size="lg"
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AvatarSelector;
